import React, { useState, useEffect, useRef, useImperativeHandle, forwardRef, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner } from '@fortawesome/free-solid-svg-icons';
import examApi from '../../services/api/examApi';
import { getLinkHref, isQuestionAnswered } from '../../services/hateoasUtils';
import { shouldPreventBackendUpdates } from '../../utils/examEndChecker';
import { QuotaExceededError, isQuotaError, getQuotaMessage } from '../../lib/quota.js';
import clsx from 'clsx';
import './ExamAnswerOptions.css';
import AssetDisplay from '../practiceTest/AssetDisplay';

/**
 * ExamAnswerOptions component - specialized version for simulated exams
 * Key differences from CleanAnswerOptions:
 * - Never shows correct/incorrect feedback during exam
 * - Allows answer changes even after submission
 * - Shows only selection state, not correctness
 * - Proper exam-mode styling and behavior
 */
const ExamAnswerOptions = forwardRef(({
  currentQuestion,
  selectedAnswer,
  setSelectedAnswer,
  isAnswerSubmitted, // Controlled by parent
  setIsAnswerSubmitted, // For backward compatibility (not used)
  isCorrect, // Controlled by parent - always false in exam mode
  setIsCorrect, // For backward compatibility (not used)
  isFlagged,
  themeStyles,
  isDarkMode,
  examSession, // Exam session for end date checking
  isLocked = false, // NEW: Disables UI when question is locked (answered/skipped)
  onAnswerSubmitted, // Callback to parent
  onAnswersLoaded, // Callback to sync answers with parent
  onNavigateNext, // Callback to navigate to next question after submission
  onSkipQuestion, // Callback to skip question without marking as answered
  submitTrigger = 0, // NEW: External trigger for submission
  hideButtons = false // NEW: Hide internal buttons when parent handles them
}, ref) => {
  // Local state - only for loading and answers data
  const [answers, setAnswers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [timeSpent, setTimeSpent] = useState(0);
  
  // Refs for tracking
  const startTimeRef = useRef(Date.now());
  const processedQuestionsRef = useRef(new Set());
  const previousPropsRef = useRef({});

  // Track selectedAnswer changes for debugging
  useEffect(() => {
    // console.log('[ExamAnswerOptions] selectedAnswer prop changed:', {
    //   selectedAnswer,
    //   isAnswerSubmitted,
    //   questionId: currentQuestion?.id,
    //   timestamp: new Date().toISOString()
    // });
  }, [selectedAnswer, isAnswerSubmitted, currentQuestion?.id]);

  // Track prop changes for debugging
  useEffect(() => {
    const currentProps = {
      questionId: currentQuestion?.id,
      selectedAnswer,
      isAnswerSubmitted,
      isCorrect
    };
    
    const previousProps = previousPropsRef.current;
    const changes = {};
    
    Object.keys(currentProps).forEach(key => {
      if (currentProps[key] !== previousProps[key]) {
        changes[key] = {
          from: previousProps[key],
          to: currentProps[key]
        };
      }
    });
    
    if (Object.keys(changes).length > 0) {
      // console.log('[ExamAnswerOptions] Props changed:', {
      //   timestamp: new Date().toISOString(),
      //   changes,
      //   fullCurrentProps: currentProps,
      //   fullPreviousProps: previousProps
      // });
    }
    
    previousPropsRef.current = currentProps;
  }, [currentQuestion?.id, selectedAnswer, isAnswerSubmitted, isCorrect]);

  // Reset when question changes
  useEffect(() => {
    // console.log('[ExamAnswerOptions] Question change effect triggered:', {
    //   questionId: currentQuestion?.id
    // });
    
    if (currentQuestion?.id) {
      startTimeRef.current = Date.now();
      processedQuestionsRef.current.clear();
      
      // console.log('[ExamAnswerOptions] Reset for new question');
    }
  }, [currentQuestion?.id]);

  // Time tracking - optimized to prevent unnecessary re-renders
  useEffect(() => {
    // Only track time if not submitted
    if (!isAnswerSubmitted) {
      const timer = setInterval(() => {
        setTimeSpent(prevTime => {
          const newTime = Math.floor((Date.now() - startTimeRef.current) / 1000);
          // Only update if time actually changed
          return prevTime !== newTime ? newTime : prevTime;
        });
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [isAnswerSubmitted]);  // Handle answer selection - allows changes even after submission in exam mode
  const handleAnswerSelect = (letter) => {
    if (submitting || isLocked) {
      // console.log('[ExamAnswerOptions] Answer selection blocked:', {
      //   submitting,
      //   isLocked,
      //   attempted: letter
      // });
      return;
    }

    // Check if exam has ended before allowing answer selection
    if (shouldPreventBackendUpdates(examSession)) {
      // console.log('[ExamAnswerOptions] Exam has ended or is finished, preventing answer selection');
      return;
    }

    // console.log('[ExamAnswerOptions] Answer selection:', {
    //   previousAnswer: selectedAnswer,
    //   newAnswer: letter,
    //   isAnswerSubmitted,
    //   questionId: currentQuestion?.id,
    //   timestamp: new Date().toISOString()
    // });

    // In exam mode, always allow selection changes
    setSelectedAnswer(letter);
    
    // If this question was previously submitted, reset the submission state
    // to allow the user to submit the new selection
    if (isAnswerSubmitted) {
      // console.log('[ExamAnswerOptions] Resetting submission state for answer change');
      // Note: We don't actually reset isAnswerSubmitted here as it's controlled by parent
      // The parent component handles this through the answer submission flow
    }
  };
  // Submit answer - MOVED UP BEFORE useImperativeHandle TO FIX INITIALIZATION ORDER
  const submitAnswer = useCallback(async () => {
    if (!selectedAnswer || submitting || isLocked) return;

    // Check if exam has ended before allowing answer submissions
    if (shouldPreventBackendUpdates(examSession)) {
      // console.log('[ExamAnswerOptions] Exam has ended or is finished, preventing answer submission');
      return;
    }

    // console.log('[ExamAnswerOptions] ======= SUBMIT ANSWER DEBUG =======');
    // console.log('[ExamAnswerOptions] Submit started with:', {
    //   selectedAnswer,
    //   timeSpent,
    //   isFlagged,
    //   questionId: currentQuestion?.id,
    //   timestamp: new Date().toISOString()
    // });

    setSubmitting(true);
    try {
      const selectedIndex = selectedAnswer.charCodeAt(0) - 65;
      const selectedAnswerObj = answers[selectedIndex];
      const isAnswerCorrect = selectedAnswerObj?.isCorrect === true;

      const payload = {
        Answers: [selectedAnswerObj.id],
        TimeSpent: timeSpent,
        IsFlagged: isFlagged || false
      };

      // console.log('[ExamAnswerOptions] Submission payload created:', payload);

      // Find submit endpoint
      let submitLink;
      
      // Method 1: Check response._links for 'save'
      if (currentQuestion?.response?._links) {
        // console.log('[ExamAnswerOptions] Method 1: Checking response._links for save');
        submitLink = getLinkHref(currentQuestion.response._links, 'save');
      }
      
      // Method 2: Check question._links for 'answer' or 'save'
      if (!submitLink && currentQuestion?._links) {
        // console.log('[ExamAnswerOptions] Method 2: Checking question._links');
        const answerLink = getLinkHref(currentQuestion._links, 'answer');
        const saveLink = getLinkHref(currentQuestion._links, 'save');
        submitLink = answerLink || saveLink;
      }
      
      // Method 3: Construct endpoint from IDs
      if (!submitLink && currentQuestion?.examId && currentQuestion?.id) {
        const constructedLink = `/api/exam/${currentQuestion.examId}/question/${currentQuestion.id}`;
        // console.log('[ExamAnswerOptions] Method 3: Constructing endpoint from IDs:', constructedLink);
        submitLink = constructedLink;
      }

      // console.log('[ExamAnswerOptions] Final submitLink:', { submitLink });

      if (!submitLink) {
        const errorMsg = 'Cannot submit answer: No save endpoint available';
        console.error('[ExamAnswerOptions] ERROR:', errorMsg);
        setError(errorMsg);
        return;
      }

      const response = await examApi.followHateoasLink(submitLink, 'POST', payload);
      const apiIsCorrect = response?.IsCorrect !== undefined ? response.IsCorrect : isAnswerCorrect;

      // console.log('[ExamAnswerOptions] API response received:', {
      //   response,
      //   apiIsCorrect,
      //   onAnswerSubmittedExists: !!onAnswerSubmitted,
      //   timestamp: new Date().toISOString()
      // });

      // Notify parent - pass the correctness data but parent won't use it for UI feedback in exam mode
      if (onAnswerSubmitted) {
        const responseObject = {
          questionId: currentQuestion?.id,
          isCorrect: apiIsCorrect,
          answerIds: [selectedAnswerObj.id],
          timeSpent: timeSpent,
          isFlagged: isFlagged || false,
          timestamp: new Date().toISOString()
        };
        
        // console.log('[ExamAnswerOptions] CALLING onAnswerSubmitted with response object:', responseObject);
        onAnswerSubmitted(responseObject);
      } else {
        console.warn('[ExamAnswerOptions] onAnswerSubmitted callback not available!');
      }

    } catch (err) {
      console.error('Error submitting answer:', err);
      // Check if this is a quota exceeded error and display detailed message
      if (isQuotaError(err)) {
        const quotaMessage = getQuotaMessage(err.quota);
        setError(quotaMessage);
      } else {
        setError(`Failed to submit answer: ${err.message}`);
      }
    } finally {
      setSubmitting(false);
    }
  }, [selectedAnswer, submitting, isLocked, timeSpent, isFlagged, currentQuestion, answers, onAnswerSubmitted]);

  // Expose submitAnswer method to parent via ref - NOW AFTER submitAnswer IS DEFINED
  useImperativeHandle(ref, () => ({
    submitAnswer: () => {
      // console.log('[ExamAnswerOptions] External submitAnswer called via ref');
      submitAnswer();
    }
  }), [submitAnswer]);

  // Watch for external submit trigger
  useEffect(() => {
    if (submitTrigger > 0) {
      // console.log('[ExamAnswerOptions] External submitTrigger received:', submitTrigger);
      if (!submitting && selectedAnswer) {
        // In exam mode, allow resubmission to change answers
        // console.log('[ExamAnswerOptions] Triggering submission via submitTrigger');
        submitAnswer();
      } else {
        // console.log('[ExamAnswerOptions] Cannot submit via trigger - invalid state:', {
        //   isAnswerSubmitted,
        //   submitting,
        //   isLocked,
        //   selectedAnswer
        // });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [submitTrigger, submitAnswer]);
  // Check if question was previously answered
  const checkPreviousAnswers = useCallback((answersData) => {
    if (!currentQuestion?.response || !answersData) return;

    const questionKey = `${currentQuestion.id}_${currentQuestion.response?.timestamp || 'no_timestamp'}`;
    if (processedQuestionsRef.current.has(questionKey)) return;
    processedQuestionsRef.current.add(questionKey);

    const questionResponse = currentQuestion.response;
    
    if (isQuestionAnswered(questionResponse)) {
      const answeredIds = questionResponse.answerIds || [];
      
      // console.log('[ExamAnswerOptions] Previous answer detected:', {
      //   questionId: currentQuestion?.id,
      //   answeredIds,
      //   isCorrect: questionResponse.isCorrect,
      //   currentSelectedAnswer: selectedAnswer
      // });      
      
      // RESTORE THE SELECTED ANSWER: Convert answer ID to letter
      if (answeredIds.length > 0 && answersData.length > 0) {
        const answerId = answeredIds[0]; // Take the first answer ID
        const answerIndex = answersData.findIndex(answer => answer.id === answerId);
        
        if (answerIndex >= 0) {
          const answerLetter = String.fromCharCode(65 + answerIndex); // Convert 0->A, 1->B, etc.
          // console.log('[ExamAnswerOptions] Restoring previous answer:', {
          //   answerId,
          //   answerIndex,
          //   answerLetter,
          //   currentSelected: selectedAnswer
          // });          // Only set if different to avoid unnecessary re-renders
          if (selectedAnswer !== answerLetter) {
            // console.log('[ExamAnswerOptions] *** ATTEMPTING TO SET SELECTED ANSWER ***:', {
            //   from: selectedAnswer,
            //   to: answerLetter,
            //   questionId: currentQuestion?.id,
            //   timestamp: new Date().toISOString()
            // });
            
            setSelectedAnswer(answerLetter);
            
            // Force a re-render to ensure UI updates with delay to see the change
            setTimeout(() => {
              // console.log('[ExamAnswerOptions] *** POST-RESTORATION CHECK ***:', {
              //   selectedAnswerAfter: selectedAnswer,
              //   expectedLetter: answerLetter,
              //   questionId: currentQuestion?.id,
              //   wasSuccessful: selectedAnswer === answerLetter
              // });
            }, 200);
          } else {
            // console.log('[ExamAnswerOptions] Selected answer already matches, no change needed:', {
            //   selectedAnswer,
            //   answerLetter
            // });
          }
        } else {
          // console.warn('[ExamAnswerOptions] Could not find answer with ID:', answerId);
        }
      }
      
      // Notify parent of previous answer
      if (onAnswerSubmitted && questionResponse.isCorrect !== undefined) {
        // Create complete response object for previously answered question
        const responseObject = {
          questionId: currentQuestion?.id,
          isCorrect: questionResponse.isCorrect,
          answerIds: answeredIds,
          timeSpent: questionResponse.timeSpent || 0,
          isFlagged: questionResponse.isFlagged || false,
          timestamp: questionResponse.timestamp || new Date().toISOString()
        };
        
        // console.log('[ExamAnswerOptions] CALLING onAnswerSubmitted for previously answered question:', responseObject);
        onAnswerSubmitted(responseObject);
      }
    }
  }, [currentQuestion, selectedAnswer, onAnswerSubmitted, setSelectedAnswer]);

  // Load answers when question changes
  useEffect(() => {
    const fetchAnswers = async () => {
      // console.log('[ExamAnswerOptions] Fetching answers for question:', currentQuestion?.id);
      
      if (!currentQuestion?.id) {
        // console.log('[ExamAnswerOptions] No question ID, setting loading false');
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        let answersData = null;

        // Try to get answers from HATEOAS link
        if (currentQuestion._links?.answers?.href) {
          try {
            // console.log('[ExamAnswerOptions] Fetching answers from HATEOAS link:', currentQuestion._links.answers.href);
            answersData = await examApi.followHateoasLink(currentQuestion._links.answers.href);
            // console.log('[ExamAnswerOptions] HATEOAS answers received:', answersData);
          } catch (err) {
            console.warn('[ExamAnswerOptions] HATEOAS link failed:', err.message);
          }
        }

        // Fallback to direct API call
        if (!answersData || !Array.isArray(answersData) || answersData.length === 0) {
          const directUrl = `/api/question/${currentQuestion.id}/answers`;
          try {
            // console.log('[ExamAnswerOptions] Fetching answers from direct API:', directUrl);
            answersData = await examApi.followHateoasLink(directUrl);
            // console.log('[ExamAnswerOptions] Direct API answers received:', answersData);
          } catch (err) {
            console.warn('[ExamAnswerOptions] Direct API call failed:', err.message);
          }
        }

        // Check embedded answers
        if (!answersData || !Array.isArray(answersData) || answersData.length === 0) {
          if (currentQuestion._embedded?.answers) {
            // console.log('[ExamAnswerOptions] Using embedded answers:', currentQuestion._embedded.answers);
            answersData = currentQuestion._embedded.answers;
          }
        }

        if (!answersData || !Array.isArray(answersData) || answersData.length === 0) {
          console.error('[ExamAnswerOptions] No answer options available');
          setError('No answer options available for this question.');
          setLoading(false);
          return;
        }
        
        // console.log('[ExamAnswerOptions] Setting answers:', {
        //   count: answersData.length,
        //   answers: answersData.map((ans, idx) => ({
        //     index: idx,
        //     id: ans.id,
        //     isCorrect: ans.isCorrect,
        //     body: ans.body?.substring(0, 50) + '...'
        //   }))
        // });
        
        setAnswers(answersData);
        
        // Notify parent component of loaded answers
        if (onAnswersLoaded) {
          // console.log('[ExamAnswerOptions] Notifying parent of loaded answers');
          onAnswersLoaded(answersData);
        }
        
        checkPreviousAnswers(answersData);
      } catch (err) {
        console.error('[ExamAnswerOptions] Error loading answers:', err);
        // Check if this is a quota exceeded error and display detailed message
        if (isQuotaError(err)) {
          const quotaMessage = getQuotaMessage(err.quota);
          setError(quotaMessage);
        } else {
          setError(`Failed to load answers: ${err.message}`);
        }
      } finally {
        setLoading(false);
      }
    };    fetchAnswers();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentQuestion?.id]); // Only depend on question ID to prevent infinite loops - other deps handled internally
  // Additional effect to ensure answer restoration when both answers and question are available
  useEffect(() => {
    if (answers.length > 0 && currentQuestion?.id && !selectedAnswer && isAnswerSubmitted) {
      // This handles cases where isAnswerSubmitted is true but selectedAnswer is null
      // This can happen when navigating back to a previously answered question
      // console.log('[ExamAnswerOptions] Attempting additional answer restoration:', {
      //   questionId: currentQuestion.id,
      //   answersLength: answers.length,
      //   selectedAnswer,
      //   isAnswerSubmitted
      // });
      
      checkPreviousAnswers(answers);
      
      // Safety mechanism: If still no selected answer after a delay, try again
      setTimeout(() => {
        if (!selectedAnswer && isAnswerSubmitted && currentQuestion?.response?.answerIds) {
          // console.log('[ExamAnswerOptions] Safety restoration attempt after delay');
          checkPreviousAnswers(answers);
        }
      }, 500);
    }
  }, [answers.length, currentQuestion?.id, selectedAnswer, isAnswerSubmitted]);

  // Skip question
  const skipQuestion = async () => {
    if (submitting || isLocked) return;

    // console.log('[ExamAnswerOptions] Skip question triggered - calling parent skip handler');
    
    if (onSkipQuestion) {
      onSkipQuestion();
    } else {
      console.warn('[ExamAnswerOptions] No onSkipQuestion handler provided, falling back to navigation');
      if (onNavigateNext) {
        onNavigateNext();
      }
    }
  };

  // Utility functions
  const getAnswerLetter = (idx) => String.fromCharCode(65 + idx);  // Build option classes for exam mode - never show correct/incorrect
  const buildOptionClasses = (ans, answerLetter) => {
    const isSelected = selectedAnswer === answerLetter;
    
    // Debug logging for the first option to track class application
    if (answerLetter === 'A') {
      // console.log('[ExamAnswerOptions] buildOptionClasses for A:', {
      //   selectedAnswer,
      //   answerLetter,
      //   isSelected,
      //   isAnswerSubmitted,
      //   timestamp: new Date().toISOString()
      // });
    }
    
    // Enhanced logic: Show visual indication if this answer is selected
    // regardless of submission state (allowing exam mode flexibility)
    const conditions = {
      // Show selected state for current selection when not submitted
      selected: isSelected && !isAnswerSubmitted,
      // Show submitted state for submitted answers
      'exam-submitted': isAnswerSubmitted && isSelected,
      // ADDITION: If this is a previously answered question and no current selection,
      // but this was the answered option, show it as submitted
      'exam-submitted-restored': !selectedAnswer && isAnswerSubmitted && 
                                currentQuestion?.response?.answerIds &&
                                answers.length > 0 &&
                                currentQuestion.response.answerIds.includes(ans.id)
    };
    
    const classes = clsx('option', conditions);
      // Debug logging for class result
    if (answerLetter === 'A') {
      // console.log('[ExamAnswerOptions] Final classes for A:', {
      //   classes,
      //   conditions,
      //   classString: classes,
      //   shouldBeHighlighted: conditions.selected || conditions['exam-submitted'] || conditions['exam-submitted-restored']
      // });
    }
    
    return classes;
  };
  // Loading state
  if (loading) {
    return (
      <div className="loading-state">
        <FontAwesomeIcon 
          icon={faSpinner} 
          spin 
          className="loading-spinner"
        />
        <div className="loading-text">Loading answer options...</div>
      </div>
    );
  }
  // Error state
  if (error) {
    return (
      <div className="error-state">
        <div className="error-message">{error}</div>
        <button
          onClick={() => window.location.reload()}
          className="error-reload-btn"
        >
          Refresh Page
        </button>
      </div>
    );
  }
  // No answers state
  if (!answers || answers.length === 0) {
    return (
      <div className="no-answers-state">
        No answer options available for this question.
      </div>
    );
  }
  return (
    <div className="exam-answer-options-container">
      {/* Answer Options using CSS classes */}
      <div className="options">{/* Show "Answer submitted" indicator when question was already answered */}
        {isAnswerSubmitted && (
          <div className="answer-submitted-indicator">
            📝 Answer submitted • You can still change your selection
          </div>
        )}
          {answers.map((ans, idx) => {
          const answerLetter = getAnswerLetter(idx);
          const isSelected = selectedAnswer === answerLetter;
          const optionClasses = buildOptionClasses(ans, answerLetter);

          // Debug logging for first option to track rendering
          if (idx === 0) {
            // console.log('[ExamAnswerOptions] Rendering option A:', {
            //   answerLetter,
            //   selectedAnswer,
            //   isSelected,
            //   isAnswerSubmitted,
            //   optionClasses,
            //   timestamp: new Date().toISOString()
            // });
          }

          return (            <div
              key={`answer-${idx}`}
              className={optionClasses}
              onClick={(e) => {
                // In exam mode, always allow answer selection/changes
                handleAnswerSelect(answerLetter);
              }}
              title="Click to select this answer (can be changed)"
            >
              <div className="option-content">                {/* Letter indicator */}
                <div className={clsx(
                  "letter-indicator",
                  isSelected && (
                    isAnswerSubmitted ? "exam-submitted" : "selected"
                  ),
                  // Handle restored state when selectedAnswer hasn't caught up yet
                  !selectedAnswer && isAnswerSubmitted && 
                  currentQuestion?.response?.answerIds &&
                  currentQuestion.response.answerIds.includes(ans.id) && "exam-submitted"
                )}>
                  {answerLetter}
                </div>

                {/* Answer text with proper CSS class styling */}
                <div className={clsx(
                  "answer-text",
                  isSelected && "selected",
                  // Handle restored state for answer text too
                  !selectedAnswer && isAnswerSubmitted && 
                  currentQuestion?.response?.answerIds &&
                  currentQuestion.response.answerIds.includes(ans.id) && "selected"
                )}>
                  {ans.bodyFormatted || ans.body || ans.text || ans.answer || ''}
                </div>
              </div>

              {/* Answer assets - images/media displayed below the answer text */}
              {ans.assets && ans.assets.length > 0 && (
                <div className="answer-asset-container">
                  <AssetDisplay 
                    assets={ans.assets}
                    isDarkMode={isDarkMode}
                    compact={true}
                  />
                </div>
              )}
            </div>
          );
        })}
      </div>      {/* Submit and Skip buttons - only show if not hidden by parent */}
      {!hideButtons && (
        <div className="answer-actions">
          {/* Skip button */}
          <button
            onClick={skipQuestion}
            disabled={submitting || isLocked}
            className="skip-btn"
          >
            {submitting ? (
              <>
                <FontAwesomeIcon icon={faSpinner} spin className="btn-spinner" />
                Skipping...
              </>
            ) : (
              'Skip'
            )}
          </button>

          {/* Submit button */}
          <button
            onClick={submitAnswer}
            disabled={!selectedAnswer || submitting || isLocked}
            className="submit-btn"
          >
            {submitting ? (
              <>
                <FontAwesomeIcon icon={faSpinner} spin className="btn-spinner" />
                {isAnswerSubmitted ? 'Updating...' : 'Submitting...'}
              </>
            ) : (
              isAnswerSubmitted ? 'Update Answer' : 'Submit Answer'
            )}
          </button>
        </div>
      )}
    </div>
  );
});

export default ExamAnswerOptions;
